import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';
import { Video, Mic, MicOff, VideoOff, PhoneOff, RefreshCw } from 'lucide-react';
import { initializeVideoCall, endVideoCall, toggleAudioVideo } from '../services/videoService';

const supabase = createClient(
  import.meta.env.VITE_SUPABASE_URL || '',
  import.meta.env.VITE_SUPABASE_ANON_KEY || ''
);

interface Expert {
  id: string;
  full_name: string;
  title: string;
}

const VideoCallPage: React.FC = () => {
  const { expertId } = useParams<{ expertId: string }>();
  const [expert, setExpert] = useState<Expert | null>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [videoEnabled, setVideoEnabled] = useState(true);
  const [isConnecting, setIsConnecting] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const remoteVideoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    loadExpert();
    setupVideoCall();

    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
      endVideoCall();
    };
  }, [expertId]);

  const loadExpert = async () => {
    try {
      const { data, error } = await supabase
        .from('experts')
        .select('id, full_name, title')
        .eq('id', expertId)
        .single();

      if (error) throw error;
      setExpert(data);
    } catch (error) {
      console.error('Error loading expert:', error);
      setError('Failed to load expert information');
    }
  };

  const setupVideoCall = async () => {
    setIsConnecting(true);
    setError(null);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { peer, stream: mediaStream, socket } = await initializeVideoCall(user.id, expertId!);
      setStream(mediaStream);
      setIsConnecting(false);

      if (localVideoRef.current) {
        localVideoRef.current.srcObject = mediaStream;
      }

      peer.on('call', (call) => {
        call.answer(mediaStream);
        call.on('stream', (remoteStream) => {
          if (remoteVideoRef.current) {
            remoteVideoRef.current.srcObject = remoteStream;
          }
        });
      });

    } catch (error) {
      console.error('Error setting up video call:', error);
      setIsConnecting(false);
      setError(error instanceof Error ? error.message : 'Failed to initialize video call');
    }
  };

  const handleRetry = () => {
    setupVideoCall();
  };

  const handleToggleAudio = () => {
    if (stream) {
      toggleAudioVideo(stream, 'audio', !audioEnabled);
      setAudioEnabled(!audioEnabled);
    }
  };

  const handleToggleVideo = () => {
    if (stream) {
      toggleAudioVideo(stream, 'video', !videoEnabled);
      setVideoEnabled(!videoEnabled);
    }
  };

  const handleEndCall = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
    endVideoCall();
    window.history.back();
  };

  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-4">
        <div className="bg-destructive/10 text-destructive p-4 rounded-lg mb-4">
          <p className="mb-2">{error}</p>
          <button
            onClick={handleRetry}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
          >
            <RefreshCw size={16} />
            Retry Connection
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto h-[calc(100vh-16rem)]">
      <div className="bg-card/50 backdrop-blur-sm border border-border rounded-lg p-4 mb-4">
        {expert && (
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Video className="text-primary" size={32} />
              <div>
                <h2 className="font-semibold">{expert.full_name}</h2>
                <p className="text-sm text-muted-foreground">{expert.title}</p>
              </div>
            </div>
            <div className="flex space-x-4">
              <button
                onClick={handleToggleAudio}
                className={`p-3 rounded-full ${
                  audioEnabled ? 'bg-primary' : 'bg-destructive'
                }`}
                disabled={isConnecting}
              >
                {audioEnabled ? (
                  <Mic className="text-primary-foreground" size={20} />
                ) : (
                  <MicOff className="text-destructive-foreground" size={20} />
                )}
              </button>
              <button
                onClick={handleToggleVideo}
                className={`p-3 rounded-full ${
                  videoEnabled ? 'bg-primary' : 'bg-destructive'
                }`}
                disabled={isConnecting}
              >
                {videoEnabled ? (
                  <Video className="text-primary-foreground" size={20} />
                ) : (
                  <VideoOff className="text-destructive-foreground" size={20} />
                )}
              </button>
              <button
                onClick={handleEndCall}
                className="p-3 rounded-full bg-destructive"
                disabled={isConnecting}
              >
                <PhoneOff className="text-destructive-foreground" size={20} />
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4 h-full relative">
        {isConnecting && (
          <div className="absolute inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-10">
            <div className="flex flex-col items-center gap-4">
              <RefreshCw className="animate-spin text-primary" size={32} />
              <p className="text-lg font-medium">Connecting to video call...</p>
            </div>
          </div>
        )}
        <div className="relative">
          <video
            ref={remoteVideoRef}
            autoPlay
            playsInline
            className="w-full h-full object-cover rounded-lg bg-black"
          />
          <div className="absolute bottom-4 right-4 w-48 h-36">
            <video
              ref={localVideoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-full object-cover rounded-lg bg-black"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoCallPage;